<div class="auth-modal">
  <button class="close-btn" (click)="closeDialog()">
    <mat-icon>close</mat-icon>
  </button>

  <div class="auth-header">
    @switch (data.method) {
      @case (AuthDialogMethod.LOGIN) {
        <h2 class="auth-title">{{ 'Log in' | translate }}</h2>
        <p class="auth-subtitle">{{ 'auth_login_subtitle' | translate }}</p>
      }
      @case (AuthDialogMethod.REGISTER) {
        <h2 class="auth-title">{{ 'Register' | translate }}</h2>
        <p class="auth-subtitle">{{ 'auth_register_subtitle' | translate }}</p>
      }
      @case (AuthDialogMethod.FORGOT_PASSWORD) {
        <h2 class="auth-title">{{ 'Forgot your password' | translate }}</h2>
        <p class="auth-subtitle">{{ 'auth_forgot_subtitle' | translate }}</p>
      }
    }
  </div>

  @switch (data.method) {
    @case (AuthDialogMethod.LOGIN) {
      <form [formGroup]="credentials" class="auth-form">
        <div class="form-field-group">
          <label>{{ 'Email' | translate }}</label>
          <mat-form-field appearance="outline">
            <input formControlName="email" matInput placeholder="email@example.com" />
          </mat-form-field>
        </div>

        <div class="form-field-group">
          <label>{{ 'password' | translate }}</label>
          <mat-form-field appearance="outline">
            <input
              formControlName="password"
              matInput
              [type]="showPassword ? 'text' : 'password'"
              placeholder="••••••••"
            />
            <mat-icon matSuffix class="toggle-visibility" (click)="togglePasswordVisibility()">
              {{ showPassword ? 'visibility' : 'visibility_off' }}
            </mat-icon>
          </mat-form-field>
        </div>

        <div class="error-message" *ngrxLet="errorMessage$ as error">
          @if (error) {
            <mat-icon class="error-icon">error_outline</mat-icon>
            <span>{{ error }}</span>
          }
        </div>
      </form>
    }
    @case (AuthDialogMethod.REGISTER) {
      <!--
        Register form collects everything in one pass: credentials
        (email + password + confirm) and profile (fullname, phone,
        birthday, gender). Submitting writes an atomic row via
        `store_new_user_with_profile`, so abandoning the tab after
        this screen can no longer leave a ghost user behind.
      -->
      <form [formGroup]="credentials" class="auth-form">
        <div class="form-field-group">
          <label>{{ 'Full name' | translate }}</label>
          <mat-form-field appearance="outline">
            <input
              formControlName="fullname"
              matInput
              [placeholder]="'Maria Papadopoulou' | translate"
            />
          </mat-form-field>
          @if (credentials.get('fullname')?.touched && credentials.get('fullname')?.invalid) {
            <span class="field-hint error">
              <mat-icon class="hint-icon">error_outline</mat-icon>
              {{ 'Please enter your full name' | translate }}
            </span>
          }
        </div>

        <div class="form-field-group">
          <label>{{ 'Email' | translate }}</label>
          <mat-form-field appearance="outline">
            <input formControlName="email" matInput placeholder="email@example.com" />
          </mat-form-field>
        </div>

        <div class="form-field-group">
          <label>{{ 'Phone' | translate }}</label>
          <mat-form-field appearance="outline">
            <input
              formControlName="phone"
              matInput
              type="tel"
              inputmode="tel"
              placeholder="69XXXXXXXX"
            />
          </mat-form-field>
          @if (credentials.get('phone')?.touched && credentials.get('phone')?.invalid) {
            <span class="field-hint error">
              <mat-icon class="hint-icon">error_outline</mat-icon>
              {{ 'Please enter a valid phone' | translate }}
            </span>
          }
        </div>

        <div class="form-field-group">
          <label>{{ 'Date of birth' | translate }}</label>
          <app-date-of-birth-picker formControlName="birthday"></app-date-of-birth-picker>
          @if (credentials.get('birthday')?.touched && credentials.get('birthday')?.invalid) {
            <span class="field-hint error">
              <mat-icon class="hint-icon">error_outline</mat-icon>
              {{ 'Please select your date of birth' | translate }}
            </span>
          }
        </div>

        <div class="form-field-group">
          <label>{{ 'Gender' | translate }}</label>
          <mat-form-field appearance="outline">
            <mat-select formControlName="gender" [placeholder]="'Select' | translate">
              @for (g of genders; track g.value) {
                <mat-option [value]="g.value">{{ g.label | translate }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
        </div>

        <div class="form-field-group">
          <label>{{ 'password' | translate }}</label>
          <mat-form-field appearance="outline">
            <input
              formControlName="password"
              matInput
              [type]="showPassword ? 'text' : 'password'"
              placeholder="••••••••"
            />
            <mat-icon
              matSuffix
              class="toggle-visibility"
              (click)="togglePasswordVisibility()"
            >
              {{ showPassword ? 'visibility' : 'visibility_off' }}
            </mat-icon>
          </mat-form-field>
        </div>

        <div class="form-field-group">
          <label>{{ 'confirm password' | translate }}</label>
          <mat-form-field appearance="outline">
            <input
              formControlName="confirmPassword"
              matInput
              [type]="showConfirmPassword ? 'text' : 'password'"
              placeholder="••••••••"
            />
            <mat-icon
              matSuffix
              class="toggle-visibility"
              (click)="toggleConfirmPasswordVisibility()"
            >
              {{ showConfirmPassword ? 'visibility' : 'visibility_off' }}
            </mat-icon>
          </mat-form-field>
          @if (passwordValid && passwordsMatch) {
            <span class="field-hint success">
              <mat-icon class="hint-icon">check_circle</mat-icon>
              {{ 'auth_passwords_match' | translate }}
            </span>
          }
        </div>

        <!-- Every club: Terms of use + Privacy policy; the club rules join in
             when the club published some. Links open a new tab so the form
             keeps what was typed. -->
        <div class="rules-agreement">
          <label class="rules-checkbox">
            <input type="checkbox" [(ngModel)]="legalAccepted" [ngModelOptions]="{ standalone: true }" />
            <span class="checkmark"></span>
            <span class="rules-text">
              {{ 'legal_accept_a' | translate }}
              <a routerLink="/terms" target="_blank" class="rules-link" (click)="$event.stopPropagation()">{{ 'legal_accept_terms' | translate }}</a>
              @if (hasClubRules) {
                {{ 'legal_accept_and' | translate }}
                <a routerLink="/club-rules" target="_blank" class="rules-link" (click)="$event.stopPropagation()">{{ 'legal_accept_rules' | translate }}</a>
              }
              {{ 'legal_accept_b' | translate }}
              <a routerLink="/privacy" target="_blank" class="rules-link" (click)="$event.stopPropagation()">{{ 'legal_accept_privacy' | translate }}</a>.
            </span>
          </label>
        </div>

        <div class="error-message" *ngrxLet="errorMessage$ as error">
          @if (error) {
            <mat-icon class="error-icon">error_outline</mat-icon>
            <span>{{ error }}</span>
          }
        </div>
      </form>
    }
    @case (AuthDialogMethod.FORGOT_PASSWORD) {
      @if (!forgotPasswordVerification) {
        <form [formGroup]="credentials" class="auth-form">
          <div class="form-field-group">
            <label>{{ 'Email' | translate }}</label>
            <mat-form-field appearance="outline">
              <input formControlName="email" matInput placeholder="email@example.com" />
            </mat-form-field>
          </div>

          <div class="error-message" *ngrxLet="errorMessage$ as error">
            @if (error) {
              <mat-icon class="error-icon">error_outline</mat-icon>
              <span>{{ error }}</span>
            }
          </div>
        </form>
      }
    }
  }

  @switch (data.method) {
    @case (AuthDialogMethod.LOGIN) {
      <div class="auth-actions">
        @if (!loading) {
          <button class="primary-btn" (click)="submitCredentials()">
            {{ 'Log in' | translate }}
          </button>
          <button class="link-btn" (click)="switchMethod(AuthDialogMethod.FORGOT_PASSWORD)">
            {{ 'I forgot my password' | translate }}
          </button>
          <div class="auth-divider">
            <span>{{ 'or' | translate }}</span>
          </div>
          @if (googleSsoEnabled) {
            <button class="google-btn" (click)="signInWithGoogle()">
              <svg width="20" height="20" viewBox="0 0 48 48">
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
              </svg>
              {{ 'Sign in with Google' | translate }}
            </button>
            <div class="auth-divider">
              <span>{{ 'or' | translate }}</span>
            </div>
          }
          <button class="secondary-btn" (click)="switchMethod(AuthDialogMethod.REGISTER)">
            {{ 'Register' | translate }}
          </button>
        } @else {
          <mat-spinner [diameter]="36"></mat-spinner>
        }
      </div>
    }
    @case (AuthDialogMethod.REGISTER) {
      <div class="auth-actions">
        @if (!loading) {
          <button class="primary-btn" [disabled]="!canRegister" (click)="submitCredentials()">
            {{ 'Register' | translate }}
          </button>
          @if (googleSsoEnabled) {
            <div class="auth-divider">
              <span>{{ 'or' | translate }}</span>
            </div>
            <button class="google-btn" [disabled]="!canRegister" (click)="signInWithGoogle()">
              <svg width="20" height="20" viewBox="0 0 48 48">
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
              </svg>
              {{ 'Sign in with Google' | translate }}
            </button>
          }
          <button class="link-btn" (click)="switchMethod(AuthDialogMethod.LOGIN)">
            {{ 'Already have an account? Login instead' | translate }}
          </button>
        } @else {
          <mat-spinner [diameter]="36"></mat-spinner>
        }
      </div>
    }
    @case (AuthDialogMethod.FORGOT_PASSWORD) {
      <div class="auth-actions">
        @if (!loading) {
          @if (!forgotPasswordVerification) {
            <button class="primary-btn" (click)="forgotPassword()">
              {{ 'Submit' | translate }}
            </button>
            <button class="link-btn" (click)="switchMethod(AuthDialogMethod.LOGIN)">
              {{ 'Back to signin' | translate }}
            </button>
          } @else {
            <div class="success-state">
              <div class="success-icon-wrapper">
                <mat-icon class="success-icon">check_circle</mat-icon>
              </div>
              <span class="success-text">
                {{ 'Password reset email sent to your email' | translate }}
              </span>
              <button class="link-btn" (click)="switchMethod(AuthDialogMethod.LOGIN)">
                {{ 'Back to signin' | translate }}
              </button>
            </div>
          }
        } @else {
          <mat-spinner [diameter]="36"></mat-spinner>
        }
      </div>
    }
  }
</div>
